import React, { useState, useEffect } from 'react';
import { Card, Stack, Button, Box, IconButton } from '@mui/material';
import { Icon } from "@iconify/react";
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/seasons.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  getDataList,
  toggleStatus,
  getListSequence,
} from '../../services/content.service';
import {deleteSeasonById, updateSeasonEpisodesSequence, updateSeasonSequence} from '../../services/series.service';
import { ACTIONS, CONTENT_ALIAS } from '../../config/const.config';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import AddEditSeason from './add-edit-season.component';
import ViewSeasons from './view-season.component';
import ROUTE_PATH from '../../config/routes.config';
import editUrl from '../../utils/url-edit.util';
import ListSequenceEpisodes from './list-sequence-episodes.component';
import ListSequenceSeasons from './list-sequence-seasons.component';

const ListSeasons = ({ dataId }) => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;
  const navigate = useNavigate();
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [action, setAction] = useState({
    show: false,
    data: '',
  });
  const [vidOptions, setVidOptions] = useState({
    error: false,
    loading: true,
  });
  const [seasonAction, setSeasonAction] = useState({
    show: false,
    data: '',
  });
  const [seasonOptions, setSeasonOptions] = useState({
    error: false,
    loading: true,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [videosList, setVideosList] = useState([]);
  const [seasonList, setSeasonList] = useState([]);

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleVideoReordering = (seasonId) => {
    setAction({ ...action, show: true, data: seasonId });
    let uri = 'episodes-season';
    uri += `?type=EPISODE`;
    uri += `&seasonId=${seasonId}`;

    getListSequence(uri)
      .then((res) => {
        setVidOptions({
          ...vidOptions,
          loading: false,
          error: false,
        });
        setVideosList(res.data);
      })
      .catch(() =>
        setVidOptions({
          ...vidOptions,
          loading: false,
          error: true,
        })
      );
  };

  const handleSeasonsReordering = (seriesId) => {
    setSeasonAction({ ...seasonAction, show: true, data: seriesId });
    let uri = 'seasons';
    uri += `?seriesId=${seriesId}`;

    getListSequence(uri)
      .then((res) => {
        setSeasonOptions({
          ...seasonOptions,
          loading: false,
          error: false,
        });
        setSeasonList(res.data);
      })
      .catch(() =>
      setSeasonOptions({
          ...seasonOptions,
          loading: false,
          error: true,
        })
      );
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (selectedAction === ACTIONS.VIEW_EPISODES.value) {
      const url = editUrl(ROUTE_PATH.CONTENT_EPISODES, selectedData.id);
      navigate(url);
    }
    if (selectedAction === ACTIONS.SEQUENCE.value) {
      handleVideoReordering(selectedData.id);
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteSeasonById(doAction.data.id)
      .then(() => {
        handleActionSuccess(
          `${CONTENT_ALIAS.SEASON_SINGULAR} deleted successfully.`
        );
      })
      .catch((e) => {
        const message =
          e.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleToggleStatus = (r, value, updateValueFor) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r.id,
      status: value ? 1 : 0,
      type: 'seasons',
      updateValueFor,
    };

    let successToggleMessage = '';
    if (payload.updateValueFor === 'status') successToggleMessage = '';

    toggleStatus(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: `${successToggleMessage} Status updated.`,
        });
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  useEffect(() => {
    let uri = `seasons?id=${dataId}`;
    uri += `&page=${options.page + 1}`;
    uri += `&perPage=${options.rowsPerPage}`;
    if (options.sortBy && options.sortOrder) {
      uri += `&sortBy=${options.sortBy}`;
      uri += `&sortOrder=${options.sortOrder}`;
    }

    getDataList(uri)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          seriesTitle: res?.data?.seriesTitle || '',
          error: false,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  const handleCloseDialog = () => {
    setAction({ ...action, show: false, data: '' });
    setVidOptions({ ...vidOptions, loading: true });
  };

  const handleCloseSeasonDialog = () => {
    setSeasonAction({ ...seasonAction, show: false, data: '' });
    setSeasonOptions({ ...seasonOptions, loading: true });
  };

  const handleOnDragEndEpisodes = (result) => {
    if (result.destination) {
      const newVideos = Array.from(videosList);
      const [draggedItem] = newVideos.splice(result.source.index, 1);

      newVideos.splice(result.destination.index, 0, draggedItem);
      setVideosList(newVideos);
    }
  };

  const handleOnDragEndSeason = (result) => {
    if (result.destination) {
      const newSeasons = Array.from(seasonList);
      const [draggedItem] = newSeasons.splice(result.source.index, 1);

      newSeasons.splice(result.destination.index, 0, draggedItem);
      setSeasonList(newSeasons);
    }
  };

  const handleUpdateVideosSequence = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const updatedVideosCategorySequence = videosList.map(
      (video) => video.episode_id
    );

    const payload = {
      seasonId: doAction.data.id,
      seriesId: videosList[0].series_id,
      newSequence: updatedVideosCategorySequence,
    };

    updateSeasonEpisodesSequence(payload)
      .then((res) => {
        if (res?.data?.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: `${CONTENT_ALIAS.EPISODE_PLURAL} sequence updated successfully.`,
          });
          handleCloseDialog();
        }
      })
      .catch(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Something went wrong.',
        });
      });
  };

  const handleUpdateSeasonsSequence = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const updatedSeasonsSequence = seasonList.map(
      (season) => season.id
    );

    const payload = {
      seriesId: seasonList[0].series_id,
      newSequence: updatedSeasonsSequence,
    };

    updateSeasonSequence(payload)
      .then((res) => {
        if (res?.data?.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: `${CONTENT_ALIAS.SEASON_PLURAL} sequence updated successfully.`,
          });
          handleCloseSeasonDialog();
        }
      })
      .catch(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Something went wrong.',
        });
      });
  };

  const handleOpenDialog = () => {
    handleSeasonsReordering(dataId);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="left"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        <Link to={ROUTE_PATH.CONTENT_SERIES}>
          <IconButton color="primary" variant="contained" >
            <Icon icon="material-symbols:arrow-back-ios-new" />
          </IconButton>
        </Link>
        <Box sx={{marginLeft:0.2}} fontWeight="bold" fontSize="20px">{`Seasons of ${
        options.seriesTitle ? `- ${options.seriesTitle}` : ''
      }`}</Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '1em',
            alignItems: 'center',
          }}
        >
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="ic:round-add" />}
            onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
          >
            Create New Season
          </Button>
        </Box>
        <div>
          <Button
            color="inherit"
            variant="contained"
            onClick={() => handleOpenDialog()}
            disabled={options.totalRows === 0}
          >
            Manage Content Order
          </Button> 
          <Button
            color="inherit"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            sx={{marginLeft:2}}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          handleToggleStatus={handleToggleStatus}
        />
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditSeason
          title={`${options.seriesTitle} - Add New ${CONTENT_ALIAS.SEASON_SINGULAR}`}
          dataId={0}
          seriesId={dataId}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          options={options}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditSeason
          title={`${options.seriesTitle} - Edit ${CONTENT_ALIAS.SEASON_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          seriesId={dataId}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          options={options}
        />
      )}

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewSeasons
          title={`${options.seriesTitle} - ${CONTENT_ALIAS.SEASON_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {action.show && (
        <ListSequenceEpisodes
          handleOnDragEndEpisodes={handleOnDragEndEpisodes}
          handleUpdateVideosSequence={handleUpdateVideosSequence}
          action={action}
          selectedSeason={doAction.data}
          seasonVideosList={videosList}
          vidOptions={vidOptions}
          handleCloseDialog={handleCloseDialog}
        />
      )}
      
      {seasonAction.show && (
        <ListSequenceSeasons
          handleOnDragEndSeason={handleOnDragEndSeason}
          handleUpdateSeasonsSequence={handleUpdateSeasonsSequence}
          action={seasonAction}
          selectedSeries={options.seriesTitle}
          seriesSeasonList={seasonList}
          seasonOptions={seasonOptions}
          handleCloseDialog={handleCloseSeasonDialog}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title={`Delete ${CONTENT_ALIAS.SEASON_SINGULAR}`}
          message={`Do you want to delete ${doAction.data.title} ${CONTENT_ALIAS.SEASON_SINGULAR}? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListSeasons.propTypes = {
  dataId: PropTypes.number.isRequired,
};

export default ListSeasons;
